"use client"

import { useTranslations } from "next-intl"
import { type HTMLAttributes, useState } from "react"
import { FcGoogle } from "react-icons/fc"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { signIn } from "@/lib/auth/client"
import { cn } from "@/utils/cn"
import { getAbsoluteUrl } from "@/utils/url"

interface JoinWithGoogleButtonProps extends HTMLAttributes<HTMLButtonElement> {}

export function JoinWithGoogleButton({ className, ...props }: JoinWithGoogleButtonProps) {
  const t = useTranslations("Navbar.links")
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)

  const handleGoogleSignIn = async () => {
    try {
      setIsGoogleLoading(true)
      await signIn.social({
        provider: "google",
        callbackURL: getAbsoluteUrl("/app"),
      })
    } catch (error) {
      setIsGoogleLoading(false)
      console.error("Google sign in failed:", error)
      if (error instanceof Error) {
        toast.error(error.message || "Failed to sign in with Google")
      } else {
        toast.error("An unexpected error occurred")
      }
    }
  }

  return (
    <Button
      className={cn(
        "h-[36px]! rounded-[14px]! border border-border bg-linear-to-br! from-gray-100! to-white! text-black transition-opacity hover:opacity-75 focus-visible:ring-offset-1 dark:from-gray-800! dark:to-gray-600! dark:text-white",
        className,
      )}
      onClick={handleGoogleSignIn}
      isLoading={isGoogleLoading}
      {...props}
    >
      <FcGoogle />
      <span className="hidden lg:block">{t("join-with-google")}</span>
      <span className="block lg:hidden">{t("sign-in")}</span>
    </Button>
  )
}
